import React from 'react';

import RenderIfVisible from '@/Utility/RenderIfVisible';
import {
  Checkbox,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  type TableCellBaseProps,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import type { PostsLoaderTypeKey, PostsLoaderTypes } from '@/Components/posts/loaders';
import { useMultiViewPickerStore } from '../../MultiViewPickerStore';
import './List.less';

export type ListChildType = Array<DataArrayType | React.ReactNode>;

// Type guard
const isDataArray = (item: DataArrayType | React.ReactNode): item is DataArrayType => {
  return (item as DataArrayType)?.child !== undefined;
};

const ListViewItem = ({ listProps }: { listProps: ListChildType }) => {
  return listProps.map((item, idx) => {
    return (
      <TableCell
        key={`list_view_item_${idx.toString()}`}
        align="left"
        component={isDataArray(item) ? item?.component : 'td'}
        scope={isDataArray(item) ? item?.scope : 'row'}
        style={isDataArray(item) ? item?.style : undefined}
      >
        {isDataArray(item) ? item?.child : item}
      </TableCell>
    );
  });
};

const FallbackItem = ({ title }: { title?: string }) => {
  return (
    <>
      <TableCell className="ListViewThumbnail" component="th" scope="row">
        <Skeleton variant="rectangular" height={120} width={140} />
      </TableCell>
      <TableCell align="left">{!!title ? <a>{title}</a> : <Skeleton variant="text" height={20} />}</TableCell>
      {[...Array(5)].map((_, i) => (
        <TableCell key={`skeleton_${i.toString()}`} align="left">
          <Skeleton variant="text" height={20} />
        </TableCell>
      ))}
    </>
  );
};

type RowCheckboxProps<T extends PostsLoaderTypeKey> = {
  id: PostsLoaderTypes[T]['paginationNodeType']['id'];
};

const RowCheckbox = <T extends PostsLoaderTypeKey>({ id }: RowCheckboxProps<T>) => {
  const updateNode = useMultiViewPickerStore((state) => state.updateNode);
  const node = useMultiViewPickerStore((state) => state.nodes.get(id));

  if (!node) return null;

  return (
    <TableCell width={1}>
      <Checkbox
        checked={node.checked}
        onChange={() => {
          updateNode(id, { ...node, checked: !node.checked });
        }}
      />
    </TableCell>
  );
};

type DataArrayType = {
  child: React.ReactNode;
  component?: React.ElementType<TableCellBaseProps>;
  scope?: string;
  style?: React.CSSProperties;
};

type RowProps<T extends PostsLoaderTypeKey> = {
  listProps: ListChildType;
  node: PostsLoaderTypes[T]['paginationNodeType'];
  isLast?: boolean;
  loadMore?: () => void;
  showCheckbox?: boolean;
};

const Row = <T extends PostsLoaderTypeKey>(props: RowProps<T>) => {
  return (
    <RenderIfVisible
      defaultHeight={120}
      fallback={<FallbackItem title={'Title'} />}
      rootElement={<tr />}
      onChange={(isVisible) => {
        if (!isVisible || !props?.isLast) return;
        props?.loadMore?.();
      }}
    >
      {props?.showCheckbox && <RowCheckbox {...props?.node} />}
      <ListViewItem listProps={props?.listProps} />
    </RenderIfVisible>
  );
};

type HeaderProps = {
  value: string;
  align?: 'left' | 'center' | 'right';
  width?: number;
  style?: React.CSSProperties;
};
export type ListViewProps<T extends PostsLoaderTypeKey> = {
  data: Array<PostsLoaderTypes[T]['paginationNodeType']>;
  headerProps: HeaderProps[];
  listProps: (item: PostsLoaderTypes[T]['paginationNodeType']) => ListChildType;
  loadMore?: () => void;
  isFallback?: boolean;
  showCheckbox?: boolean;
};

const ListView = <T extends PostsLoaderTypeKey>({
  data,
  headerProps,
  listProps,
  loadMore,
  isFallback,
  showCheckbox,
}: ListViewProps<T> & { type: T }) => {
  const isSelectAll = useMultiViewPickerStore((state) => state.isSelectAll);
  const count = useMultiViewPickerStore((state) => state.selectedNodesCount);
  const selectAll = useMultiViewPickerStore((state) => state.selectAll);
  const deselectAll = useMultiViewPickerStore((state) => state.deselectAll);

  return (
    <TableContainer className="list-view-table" component={Paper}>
      <Table sx={{ minWidth: 650 }} size="small" aria-label="simple table">
        <TableHead className="list-view-header">
          <TableRow>
            {/* select all */}
            {showCheckbox && (
              <TableCell width={5}>
                <Checkbox
                  style={{ ...(isSelectAll ? {} : { color: 'white' }) }}
                  indeterminate={!isSelectAll && count > 0}
                  checked={isSelectAll}
                  onChange={isSelectAll ? deselectAll : selectAll}
                />
              </TableCell>
            )}
            {headerProps.map((headerItem) => (
              <TableCell
                key={headerItem.value}
                width={headerItem?.width || 'auto'}
                align={headerItem?.align || 'left'}
                style={headerItem.style}
              >
                {headerItem.value}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item, idx) => {
            return (
              <Row
                key={`dash_table_list_view_${idx.toString()}`}
                listProps={listProps(item)}
                loadMore={loadMore}
                node={item}
                showCheckbox={showCheckbox}
                isLast={idx === data.length - 1}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ListViewComponent = React.memo(ListView);

export { ListViewComponent as ListView };
export default ListViewComponent;
